import { BrowserEntropyCollector } from '../../../app/browser-entropy-collector.js';
import { encryptFile, decryptFile, hashBytes, generateKeyPair, runSelfTest } from '../../../app/crypto-service.js';
import { registerSessionWipeHandler } from '../../../app/session-wipe.js';
import { setWorkflowBusy, readFileAsUint8Array, download, formatFileSize } from '../../../utils.js';
import { createBundlePayloadFromFiles, isBundlePayload, parseBundlePayload } from '../bundle-payload.js';
import { log, logError, logSuccess, logWarning } from './logging.js';
import { renderSuccessCallout, renderWarningCallout } from './callout.js';

import { showToast } from './toast.js';
import { syncArchiveReviewHub } from './archive-review-hub.js';
import { clearArchiveInspector, renderArchiveInspectorPanel } from '../qcont/archive-inspector-ui.js';
import { bindRsParamsUI } from '../qcont/rs-params-display.js';
import { clearArtifactIntakeTable, renderArtifactIntakeTable } from './artifact-intake.js';
import { createSuccessorShardInputRefreshController } from '../qcont/successor-workflow-input.js';
import { initRovingTablist } from './tabs.js';

// Pro mode state
let collectedUserEntropy = null;
let entropyCollector = null;
let proSecretEpoch = 0;
let proReviewStatusSeq = 0;
let unregisterUiSessionWipe = null;

function wipeCollectedUserEntropy() {
    if (collectedUserEntropy instanceof Uint8Array) {
        collectedUserEntropy.fill(0);
    }
    collectedUserEntropy = null;
}

function wipeProSessionSecrets() {
    proSecretEpoch += 1;
    wipeCollectedUserEntropy();
    if (entropyCollector && typeof entropyCollector.dispose === 'function') {
        entropyCollector.dispose();
    }
    entropyCollector = null;
}

export function updateSidebarStatus(publicKeyLoaded, privateKeyLoaded) {
    const sysDot = document.getElementById('sys-status-dot');
    const sysText = document.getElementById('sys-status-text');
    const pubStatus = document.getElementById('ctx-pub-status');
    const secStatus = document.getElementById('ctx-sec-status');

    const hasPub = Boolean(publicKeyLoaded);
    const hasSec = Boolean(privateKeyLoaded);

    if (pubStatus) {
        if (hasPub) {
            pubStatus.textContent = 'Loaded';
            pubStatus.className = 'context-val status-success';
        } else {
            pubStatus.textContent = 'Not Loaded';
            pubStatus.className = 'context-val status-muted';
        }
    }

    if (secStatus) {
        if (hasSec) {
            secStatus.textContent = 'Loaded';
            secStatus.className = 'context-val status-warning';
        } else {
            secStatus.textContent = 'Not Loaded';
            secStatus.className = 'context-val status-muted';
        }
    }

    if (sysDot && sysText) {
        sysDot.className = 'status-indicator';
        let statusLabel = 'No key loaded';
        if (hasSec) {
            sysDot.classList.add('private-key-loaded');
            sysText.textContent = 'Private key loaded in memory';
            statusLabel = 'Private key loaded in memory';
        } else if (hasPub) {
            sysDot.classList.add('public-key-loaded');
            sysText.textContent = 'Public key loaded';
            statusLabel = 'Public key loaded';
        } else {
            sysDot.classList.add('no-key-loaded');
            sysText.textContent = 'No key loaded';
        }
        sysDot.setAttribute('aria-label', `System status: ${statusLabel}`);
    }
}

export function initUI() {
    if (!unregisterUiSessionWipe) {
        unregisterUiSessionWipe = registerSessionWipeHandler(() => {
            wipeProSessionSecrets();
        });
    }

    const el = (id) => document.getElementById(id);

    const privKeyInput = el('privKeyInput');
    const pubKeyInput = el('pubKeyInput');
    const dataFileInput = el('dataFileInput');
    const qencForQcontInput = el('qencForQcontInput');
    const privKeyForQcontInput = el('privKeyForQcontInput');
    const rsNInput = el('rsN');
    const rsKInput = el('rsK');
    const rsTextEl = el('rsText');
    const rsRuleN = el('rsRuleN');
    const rsRuleRange = el('rsRuleRange');
    const rsRuleEven = el('rsRuleEven');
    const rsSegData = el('rsSegData');
    const rsSegParity = el('rsSegParity');
    const rsMarker = el('rsMarker');
    const qcontShardsInput = el('qcontShardsInput');
    const proReviewInput = el('proReviewInput');
    const restoreQcontBtn = el('restoreQcontBtn');
    const genKeyBtn = el('genKeyBtn');
    const encBtn = el('encBtn');
    const decBtn = el('decBtn');
    const sidebarSelftestBtn = el('sidebar-selftest');
    const proEncStrategyGroup = el('proEncStrategyGroup');
    const proEncStrategyPerFile = el('proEncStrategyPerFile');
    const proEncryptionWarning = el('proEncryptionWarning');
    let currentProWorkflowTab = 'proTabDistribution';

    function policyLabel(value) {
        if (value === 'integrity-only') return 'integrity-only';
        if (value === 'any-signature') return 'any-signature';
        return 'strong-pq-signature';
    }

    function selectedFileCount(input) {
        return input?.files?.length || 0;
    }

    function selectedArtifactText(count, label) {
        return `${count} ${label} file${count === 1 ? '' : 's'}`;
    }

    function compactThresholdStatus(text) {
        const value = String(text || '').trim();
        if (!value || value.includes('Params info')) return 'not configured';
        const match = value.match(/Total:\s*n=(\d+).*?Data:\s*k=(\d+).*?Threshold:\s*t=(\d+)/i);
        if (match) return `n=${match[1]} / k=${match[2]} / t=${match[3]}`;
        return value.length > 72 ? `${value.slice(0, 69)}...` : value;
    }

    function compactEvidenceStatus(text) {
        const value = String(text || '').trim();
        if (!value) return '';
        const artifactSelectionMatch = value.match(/^(\d+)\s+(.+?)\s+artifacts?\s+selected$/i);
        if (artifactSelectionMatch) {
            return `${artifactSelectionMatch[1]} ${artifactSelectionMatch[2]} files`;
        }
        if (/^Ready:\s*\d+\/\d+/.test(value)) {
            const shardMatch = value.match(/Ready:\s*(\d+\/\d+)/);
            const policyMatch = value.match(/Policy:\s*([^.;]+)/i);
            const parts = [];
            if (shardMatch) parts.push(`${shardMatch[1]} shards`);
            if (policyMatch) parts.push(policyMatch[1].trim());
            if (/selection required/i.test(value)) parts.push('selection needed');
            else if (/selection complete/i.test(value)) parts.push('selection ready');
            if (/signature|evidence/i.test(value)) parts.push('evidence loaded');
            return parts.join(' / ') || value;
        }
        return value.length > 80 ? `${value.slice(0, 77)}...` : value;
    }

    function updateProWorkflowStatus(activeTabId = currentProWorkflowTab) {
        currentProWorkflowTab = activeTabId || currentProWorkflowTab;
        const workflowNames = {
            proTabDistribution: 'Create',
            proTabReview: 'Review Artifacts',
            proTabAttach: 'Maintain',
            proTabReshare: 'Evolve',
            proTabRestore: 'Restore',
        };
        const activeEl = el('proWorkflowStatusActive');
        const archiveEl = el('proWorkflowStatusArchive');
        const policyEl = el('proWorkflowStatusPolicy');
        const thresholdEl = el('proWorkflowStatusThreshold');
        const evidenceEl = el('proWorkflowStatusEvidence');

        if (activeEl) activeEl.textContent = workflowNames[currentProWorkflowTab] || 'Create';

        const qencCount = selectedFileCount(qencForQcontInput);
        const restoreCount = selectedFileCount(qcontShardsInput);
        const reviewCount = selectedFileCount(proReviewInput);
        const attachCount = selectedFileCount(el('qcontAttachInput'));
        const reshareCount = selectedFileCount(el('qcontReshareInput'));
        if (archiveEl) {
            if (currentProWorkflowTab === 'proTabReview' && reviewCount > 0) archiveEl.textContent = selectedArtifactText(reviewCount, 'review');
            else if (currentProWorkflowTab === 'proTabAttach' && attachCount > 0) archiveEl.textContent = selectedArtifactText(attachCount, 'maintenance');
            else if (currentProWorkflowTab === 'proTabReshare' && reshareCount > 0) archiveEl.textContent = selectedArtifactText(reshareCount, 'evolution');
            else if (currentProWorkflowTab === 'proTabRestore' && restoreCount > 0) archiveEl.textContent = selectedArtifactText(restoreCount, 'restore');
            else if (restoreCount > 0) archiveEl.textContent = selectedArtifactText(restoreCount, 'restore');
            else if (reviewCount > 0) archiveEl.textContent = selectedArtifactText(reviewCount, 'review');
            else if (attachCount > 0) archiveEl.textContent = selectedArtifactText(attachCount, 'maintenance');
            else if (reshareCount > 0) archiveEl.textContent = selectedArtifactText(reshareCount, 'evolution');
            else if (qencCount > 0) archiveEl.textContent = 'encrypted container selected';
            else archiveEl.textContent = 'No archive selected';
        }

        if (policyEl) policyEl.textContent = policyLabel(el('proAuthPolicy')?.value || 'strong-pq-signature');
        if (thresholdEl) {
            const thresholdText = String(rsTextEl?.textContent || '').trim();
            thresholdEl.textContent = compactThresholdStatus(thresholdText);
        }
        if (evidenceEl) {
            const attachText = String(el('attachStatusText')?.textContent || '').trim();
            const restoreText = String(el('proShardsStatusText')?.textContent || '').trim();
            const reviewText = String(el('proReviewStatusText')?.textContent || '').trim();
            const reshareText = String(el('proReshareStatusText')?.textContent || '').trim();
            if (currentProWorkflowTab === 'proTabAttach' && attachText) evidenceEl.textContent = compactEvidenceStatus(attachText);
            else if (currentProWorkflowTab === 'proTabReview' && reviewText) evidenceEl.textContent = compactEvidenceStatus(reviewText);
            else if (currentProWorkflowTab === 'proTabReshare' && reshareText) evidenceEl.textContent = compactEvidenceStatus(reshareText);
            else if (restoreText && restoreCount > 0) evidenceEl.textContent = compactEvidenceStatus(restoreText);
            else evidenceEl.textContent = 'not loaded';
        }
    }

    function observeProWorkflowStatusSource(id) {
        const source = el(id);
        if (!source || typeof MutationObserver === 'undefined') return;
        const observer = new MutationObserver(() => updateProWorkflowStatus());
        observer.observe(source, {
            attributes: true,
            childList: true,
            characterData: true,
            subtree: true,
        });
    }

    // Update Pro file list display
    function updateProFilesList() {
        const filesList = el('proFilesList');
        if (!filesList || !dataFileInput) return;
        
        if (!dataFileInput.files || dataFileInput.files.length === 0) {
            filesList.style.display = 'none';
            if (proEncStrategyGroup) proEncStrategyGroup.style.display = 'none';
            updateProWorkflowStatus();
            return;
        }
        
        filesList.replaceChildren();
        for (const file of dataFileInput.files) {
            const item = document.createElement('div');
            item.className = 'file-item';

            const fileName = document.createElement('span');
            fileName.className = 'file-name';
            fileName.textContent = `📄 ${file.name}`;

            const fileSize = document.createElement('span');
            fileSize.className = 'file-size';
            fileSize.textContent = formatFileSize(file.size);

            item.append(fileName, fileSize);
            filesList.appendChild(item);
        }
        filesList.style.display = 'block';
        if (proEncStrategyGroup) {
            proEncStrategyGroup.style.display = dataFileInput.files.length > 1 ? 'block' : 'none';
        }
        updateProWorkflowStatus();
    }

    const proRestoreShardRefresh = createSuccessorShardInputRefreshController({
        prefix: 'restore',
        inputId: 'qcontShardsInput',
        statusDivId: 'proShardsStatus',
        statusTextId: 'proShardsStatusText',
        actionButtonId: 'restoreQcontBtn',
        intakeContainerId: 'restoreArtifactIntake',
        intakeLabel: 'Restore artifact intake',
        intakeWorkflow: 'restore',
        preflightContainerId: 'restorePreflightDecision',
        expectedSignerInputId: 'restoreExpectedEdSigner',
        onAfterRefresh() {
            updateProWorkflowStatus();
        },
    });

    // Update Pro shards status
    async function updateProShardsStatus() {
        await proRestoreShardRefresh.refresh();
    }

    async function updateProReviewStatus() {
        const statusDiv = el('proReviewStatus');
        const statusText = el('proReviewStatusText');
        if (!statusDiv || !statusText || !proReviewInput) return;
        const files = [...(proReviewInput.files || [])];
        const requestId = ++proReviewStatusSeq;
        if (files.length === 0) {
            clearArtifactIntakeTable('reviewArtifactIntake');
            clearArchiveInspector('reviewArchiveInspector');
            statusDiv.className = 'shards-status initially-hidden';
            statusDiv.style.display = 'none';
            statusText.textContent = '0 review files selected';
            updateProWorkflowStatus();
            syncArchiveReviewHub('review', null, { collapseWhenClear: true });
            return;
        }

        statusDiv.classList.remove('initially-hidden');
        statusDiv.style.display = 'block';
        statusDiv.className = 'shards-status unknown';
        statusText.textContent = `${files.length} review artifact${files.length === 1 ? '' : 's'} selected`;
        await renderArtifactIntakeTable({
            containerId: 'reviewArtifactIntake',
            files,
            workflow: 'restore',
            label: 'Review artifact intake',
        });
        if (requestId !== proReviewStatusSeq) return;
        await renderArchiveInspectorPanel('reviewArchiveInspector', { files });
        updateProWorkflowStatus();
        syncArchiveReviewHub('review');
    }

    function updateProEncryptionControls() {
        const hasPublicKey = Boolean(pubKeyInput?.files?.[0]);
        const hasPrivateKey = Boolean(privKeyInput?.files?.[0]);

        if (encBtn) encBtn.disabled = !hasPublicKey;
        if (decBtn) decBtn.disabled = !hasPrivateKey;
        updateSidebarStatus(hasPublicKey, hasPrivateKey);

        if (!proEncryptionWarning) return;

        let message;

        if (!hasPublicKey && !hasPrivateKey) {
            message = 'Load keys in Key Management: public key for encryption and private key for decryption.';
            renderWarningCallout(proEncryptionWarning, { body: message });
        } else if (!hasPublicKey) {
            message = 'Encryption is blocked: load a public key in Key Management.';
            renderWarningCallout(proEncryptionWarning, { body: message });
        } else if (!hasPrivateKey) {
            message = 'Decryption is blocked: load a private key in Key Management.';
            renderWarningCallout(proEncryptionWarning, { body: message });
        } else {
            message = 'Keys are loaded. Encryption and decryption are available.';
            renderSuccessCallout(proEncryptionWarning, { body: message });
        }
    }

    function getEncryptionStrategy() {
        if (proEncStrategyPerFile?.checked) return 'per-file';
        return 'pack';
    }
    
    // Set up file input event listeners
    dataFileInput?.addEventListener('change', updateProFilesList);
    qcontShardsInput?.addEventListener('change', () => { void updateProShardsStatus(); });
    proReviewInput?.addEventListener('change', () => { void updateProReviewStatus(); });
    qencForQcontInput?.addEventListener('change', () => updateProWorkflowStatus());
    privKeyForQcontInput?.addEventListener('change', () => updateProWorkflowStatus());
    el('qcontAttachInput')?.addEventListener('change', () => updateProWorkflowStatus());
    el('qcontReshareInput')?.addEventListener('change', () => updateProWorkflowStatus());
    el('proAuthPolicy')?.addEventListener('change', () => updateProWorkflowStatus());
    pubKeyInput?.addEventListener('change', updateProEncryptionControls);
    privKeyInput?.addEventListener('change', updateProEncryptionControls);

    const splitRsParamsUpdate = bindRsParamsUI({
        nInput: rsNInput,
        kInput: rsKInput,
        summaryEl: rsTextEl,
        ruleN: rsRuleN,
        ruleRange: rsRuleRange,
        ruleEven: rsRuleEven,
        segData: rsSegData,
        segParity: rsSegParity,
        marker: rsMarker,
        dataLabel: el('rsDataLabel'),
        parityLabel: el('rsParityLabel'),
        markerLabel: el('rsMarkerLabel'),
        ticks: el('rsTicks'),
    }).update;
    void updateProShardsStatus();
    void updateProReviewStatus();
    updateProEncryptionControls();
    updateProFilesList();
    rsNInput?.addEventListener('input', () => updateProWorkflowStatus());
    rsKInput?.addEventListener('input', () => updateProWorkflowStatus());
    observeProWorkflowStatusSource('rsText');
    observeProWorkflowStatusSource('proSplitReadinessText');
    observeProWorkflowStatusSource('proShardsStatusText');
    observeProWorkflowStatusSource('proReviewStatusText');
    observeProWorkflowStatusSource('attachStatusText');
    observeProWorkflowStatusSource('proReshareStatusText');

    // Advanced entropy collection
    const advancedEntropyBtn = el('advancedEntropyBtn');
    const entropyStatus = el('entropyStatus');
    const entropyText = el('entropyText');
    const entropyBar = el('entropyBar');

    async function collectAdvancedEntropy() {
        if (entropyCollector) return;

        const entropyHeuristicNote = el('entropyHeuristicNote');
        let progressInterval = null;
        let collectionSucceeded = false;
        const collectionEpoch = proSecretEpoch;
        const collector = new BrowserEntropyCollector();

        setWorkflowBusy('proViewIdentity', true);
        try {
            entropyStatus.style.display = 'block';
            entropyText.textContent = 'Move mouse, type, or resize the window — collecting supplemental entropy (optional).';
            advancedEntropyBtn.textContent = '🎲 Collecting…';
            entropyHeuristicNote?.classList.remove('initially-hidden');

            entropyCollector = collector;

            progressInterval = setInterval(() => {
                if (entropyCollector !== collector) {
                    return;
                }
                const progress = collector.getProgress();
                entropyBar.style.width = `${progress.percentage}%`;
                entropyText.textContent = `Heuristic collection progress: ${progress.percentage}% · Events ${progress.collected}/${progress.required} · Queue ${progress.queueSize}`;
            }, 200);

            const collectedSeed = await collector.startCollection();
            if (!(collectedSeed instanceof Uint8Array) || collectedSeed.length === 0) {
                throw new Error('Advanced entropy collector returned no seed');
            }
            if (collectionEpoch !== proSecretEpoch || entropyCollector !== collector) {
                collectedSeed.fill(0);
                const error = new Error('Entropy collection completed after the session ended');
                error.name = 'AbortError';
                throw error;
            }
            wipeCollectedUserEntropy();
            collectedUserEntropy = collectedSeed;

            collector.wipeSensitiveState();
            if (entropyCollector === collector) entropyCollector = null;

            collectionSucceeded = true;
            entropyText.textContent = '✅ Supplemental entropy collected and mixed into next key generation.';
            entropyBar.style.width = '100%';
            advancedEntropyBtn.textContent = '✅ Supplemental entropy ready';
            advancedEntropyBtn.style.backgroundColor = '#28a745';

        } catch (error) {
            log(`Entropy collection failed: ${error.message}`);
            entropyText.textContent = '❌ Supplemental collection failed. Next key generation will use the browser CSPRNG only.';
            advancedEntropyBtn.textContent = 'Collect supplemental entropy (optional)';
            entropyHeuristicNote?.classList.remove('initially-hidden');
            collector.dispose();
            if (entropyCollector === collector) entropyCollector = null;
        } finally {
            if (progressInterval !== null) {
                clearInterval(progressInterval);
            }
            setWorkflowBusy('proViewIdentity', false);
            if (collectionSucceeded) {
                advancedEntropyBtn.disabled = true;
            }
        }
    }

    advancedEntropyBtn?.addEventListener('click', collectAdvancedEntropy);

    let selfTestRunning = false;
    async function runSidebarSelfTest() {
        if (selfTestRunning) return;
        selfTestRunning = true;

        if (sidebarSelftestBtn) {
            sidebarSelftestBtn.disabled = true;
            sidebarSelftestBtn.textContent = 'Self-test...';
        }

        try {
            log('Running cryptographic self-test suite...');
            showToast('Running self-test...', 'info', 2200);

            const report = await runSelfTest({
                onProgress: (done, total, currentCase) => {
                    if (done === 0 || done === total || (done % 10) === 0) {
                        log(`Self-test ${done}/${total}: ${currentCase}`);
                    }
                }
            });

            if (report.ok) {
                logSuccess(`Self-test PASS (${report.passed}/${report.total})`);
                showToast(`Self-test PASS (${report.passed}/${report.total})`, 'success', 4500);
            } else {
                logError(`Self-test FAIL (${report.passed}/${report.total})`, { skipToast: true });
                for (const failure of report.results.filter((item) => !item.ok)) {
                    logError(`Self-test case failed: ${failure.name} :: ${failure.error}`, { skipToast: true });
                }
                showToast(`Self-test FAIL (${report.passed}/${report.total})`, 'error', 6500);
            }
        } catch (error) {
            logError(`Self-test failed to run: ${error?.message ?? error}`);
        } finally {
            selfTestRunning = false;
            if (sidebarSelftestBtn) {
                sidebarSelftestBtn.disabled = false;
                sidebarSelftestBtn.textContent = 'Run Self-test';
            }
        }
    }

    sidebarSelftestBtn?.addEventListener('click', () => {
        void runSidebarSelfTest();
    });

    // --- Event Handlers ---
    genKeyBtn?.addEventListener('click', async () => {
        if (privKeyInput?.files?.length || pubKeyInput?.files?.length) {
            if (!confirm("A private key or public key is already loaded. Generating a new one will replace the active session. Continue?")) {
                return;
            }
        }
        
        let generatedPrivateKey = null;
        let generatedPublicKey = null;
        setWorkflowBusy('proViewIdentity', true);
        try {
            log('Generating ML-KEM-1024 keypair...');
            
            const hasCollectedEntropy = collectedUserEntropy instanceof Uint8Array && collectedUserEntropy.length > 0;
            if (hasCollectedEntropy) {
                log('Mixing crypto.getRandomValues() output with supplemental collected entropy for the next key generation.');
            } else {
                log('Using crypto.getRandomValues() only for key generation (browser CSPRNG baseline).');
            }
            
            // Generate keys with collected entropy if available
            const { privateKey, publicKey, seedInfo } = await generateKeyPair({
                userEntropyBytes: hasCollectedEntropy ? collectedUserEntropy : null
            });
            generatedPrivateKey = privateKey;
            generatedPublicKey = publicKey;
            wipeCollectedUserEntropy();

            log(`Entropy source: ${seedInfo.source}${seedInfo.hasUserEntropy ? ' (supplemental user-event mixing applied)' : ''}`);
            log(`Generated privateKey.qkey (${privateKey.length} B) and publicKey.qkey (${publicKey.length} B).`);
            
            download(new Blob([privateKey]), 'privateKey.qkey');
            download(new Blob([publicKey]), 'publicKey.qkey');
            logSuccess('Keys generated and downloaded successfully.');
            showToast('New keypair generated and downloaded.', 'success');
            
            // Clear inputs if they had files
            if (privKeyInput) privKeyInput.value = '';
            if (pubKeyInput) pubKeyInput.value = '';
            updateSidebarStatus(true, true);
            
            // Reset entropy collection UI state
            if (advancedEntropyBtn) {
                advancedEntropyBtn.textContent = 'Collect supplemental entropy (optional)';
                advancedEntropyBtn.style.backgroundColor = '';
                advancedEntropyBtn.disabled = false;
            }
            if (entropyStatus) {
                entropyStatus.style.display = 'none';
            }
            el('entropyHeuristicNote')?.classList.add('initially-hidden');
            
        } catch (e) { logError(e); } finally {
            generatedPrivateKey?.fill(0);
            generatedPublicKey?.fill(0);
            setWorkflowBusy('proViewIdentity', false);
            updateProEncryptionControls();
            splitRsParamsUpdate();
            void updateProShardsStatus();
        }
    });

    encBtn?.addEventListener('click', async () => {
        if (!pubKeyInput?.files?.[0]) { showToast('Load a public key in Key Management.', 'warning'); return; }
        if (!dataFileInput?.files?.length) { showToast('Please select file(s) to encrypt.', 'warning'); return; }
        let publicKey = null;
        setWorkflowBusy('proViewEncryption', true);
        try {
            publicKey = await readFileAsUint8Array(pubKeyInput.files[0]);
            const selectedFiles = [...dataFileInput.files];
            const strategy = getEncryptionStrategy();

            if (strategy === 'pack' && selectedFiles.length > 1) {
                const totalInputSize = selectedFiles.reduce((acc, file) => acc + file.size, 0);
                let bundleBytes = null;
                let encBytes = null;
                try {
                    const bundle = await createBundlePayloadFromFiles(selectedFiles);
                    bundleBytes = bundle.bundleBytes;
                    log(`Packing ${bundle.fileCount} files (${formatFileSize(totalInputSize)}) into one container...`);

                    const encBlob = await encryptFile(bundleBytes, publicKey, bundle.bundleName);
                    encBytes = await readFileAsUint8Array(encBlob);
                    const encHash = await hashBytes(encBytes);
                    const outName = `${bundle.bundleName}.qenc`;

                    download(encBlob, outName);
                    log(`✅ Bundle encrypted: ${outName} (${encBlob.size} B) SHA3-512=${encHash}`);
                } finally {
                    bundleBytes?.fill(0);
                    encBytes?.fill(0);
                }
            } else {
                if (strategy === 'per-file' && selectedFiles.length > 1) {
                    log('Encrypting each selected file into a separate container...');
                }
                for (const file of selectedFiles) {
                    log(`Encrypting file ${file.name} (${file.size} B)...`);
                    let fileBytes = null;
                    let encBytes = null;
                    try {
                        fileBytes = await readFileAsUint8Array(file);
                        const encBlob = await encryptFile(fileBytes, publicKey, file.name);
                        encBytes = await readFileAsUint8Array(encBlob);
                        const encHash = await hashBytes(encBytes);
                        download(encBlob, `${file.name}.qenc`);
                        log(`✅ File encrypted: ${file.name}.qenc (${encBlob.size} B) SHA3-512=${encHash}`);
                    } finally {
                        fileBytes?.fill(0);
                        encBytes?.fill(0);
                    }
                }
            }
        } catch (e) { logError(e); } finally {
            publicKey?.fill(0);
            setWorkflowBusy('proViewEncryption', false);
            updateProEncryptionControls();
            splitRsParamsUpdate();
            void updateProShardsStatus();
            dataFileInput.value = '';
            updateProFilesList();
        }
    });

    decBtn?.addEventListener('click', async () => {
        if (!privKeyInput?.files?.[0]) { showToast('Load a private key in Key Management.', 'warning'); return; }
        if (!dataFileInput?.files?.length) { showToast('Please select file(s) to decrypt (.qenc).', 'warning'); return; }
        let privateKey = null;
        setWorkflowBusy('proViewEncryption', true);
        try {
            privateKey = await readFileAsUint8Array(privKeyInput.files[0]);
            for (const file of dataFileInput.files) {
                if (!file.name.toLowerCase().endsWith('.qenc')) { log(`Skipping file ${file.name} as it is not a .qenc container.`); continue; }
                log(`Decrypting file ${file.name} (${file.size} B)...`);
                let containerBytes = null;
                let decBytes = null;
                try {
                    containerBytes = await readFileAsUint8Array(file);
                    const containerHash = await hashBytes(containerBytes);
                    log(`Container hash: SHA3-512=${containerHash}`);
                    const { decryptedBlob, metadata } = await decryptFile(containerBytes, privateKey);
                    decBytes = await readFileAsUint8Array(decryptedBlob);
                    const decHash = await hashBytes(decBytes);

                    if (isBundlePayload(decBytes)) {
                        const entries = parseBundlePayload(decBytes);
                        for (const entry of entries) {
                            download(new Blob([entry.bytes]), entry.name);
                        }
                        log(`✅ Bundle decrypted: extracted ${entries.length} file(s) from ${file.name}`);
                    } else {
                        // Use original filename from metadata if available, otherwise fallback to stripping .qenc
                        let outName = metadata.originalFilename;
                        if (!outName) {
                            // Backward compatibility: strip .qenc extension from input filename
                            outName = file.name.replace(/\.qenc$/i, '');
                        }
                        download(decryptedBlob, outName);
                        log(`✅ File decrypted: ${outName} (${decryptedBlob.size} B)`);
                    }
                    log(`Original file hash (from metadata): ${metadata.fileHash}`);
                    log(`Hash of decrypted content: ${decHash}`);
                    if (metadata.fileHash === decHash) {
                        log('Hashes match. File integrity verified.');
                    } else {
                        logWarning('Hashes do NOT match. File may be corrupted.');
                    }
                    log(`Encrypted on (UTC): ${metadata.timestamp}`);
                } finally {
                    decBytes?.fill(0);
                    containerBytes?.fill(0);
                }
            }
        } catch (e) { logError(e); } finally {
            privateKey?.fill(0);
            setWorkflowBusy('proViewEncryption', false);
            updateProEncryptionControls();
            splitRsParamsUpdate();
            void updateProShardsStatus();
            dataFileInput.value = '';
            updateProFilesList();
        }
    });

    function initProTabs() {
        const tabs = {
            proTabDistribution: ['proViewIdentity', 'proViewEncryption', 'proViewDistribution'],
            proTabReview: 'proViewReview',
            proTabAttach: 'proViewAttach',
            proTabReshare: 'proViewReshare',
            proTabRestore: 'proViewRestore',
        };
        const tabIds = Object.keys(tabs);

        initRovingTablist({
            tabIds,
            panelByTabId: tabs,
            onActivate: (tabId) => {
                if (tabId === 'proTabDistribution') {
                    setTimeout(() => {
                        rsNInput?.dispatchEvent(new Event('input'));
                    }, 10);
                }
                updateProWorkflowStatus(tabId);
            },
        });
    }

    initProTabs();
    updateProWorkflowStatus();
}
